import styled, { keyframes, css } from 'styled-components';
import { media } from './breakpoints';

export const BoardTitle = styled.h1`
  font-size: 1.25rem;
  color: ${props => props.theme.colors.iconColor};
  margin: 0;
  font-weight: 700;
  text-align: left;
  padding: 0.25rem 0;
  margin-bottom: 0.5rem;

  @media (max-width: 768px) {
    font-size: 1.05rem;
  }
`;

export const FloatingActionButton = styled.button<{ isOpen: boolean }>`
  position: fixed;
  bottom: 20px;
  right: 20px;
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: ${props => props.theme.colors.primary};
  color: white;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 1000;
  transform: ${props => props.isOpen ? 'rotate(45deg)' : 'rotate(0deg)'};

  &:hover {
    transform: ${props => props.isOpen ? 'rotate(45deg) scale(1.1)' : 'rotate(0deg) scale(1.1)'};
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.2);
  }

  &:active {
    transform: ${props => props.isOpen ? 'rotate(45deg) scale(0.95)' : 'rotate(0deg) scale(0.95)'};
  }

  @media (max-width: 768px) {
    width: 48px;
    height: 48px;
    bottom: 16px;
    right: 16px;
    font-size: 1.3rem;
  }

  @media (max-width: 723px) {
    display: none;
  }

  ${media.tabletOnly} {
    right: 16px;
    bottom: 16px;
  }
`;

export const FloatingMenuOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.1);
  z-index: 998;

  @media (max-width: 723px) {
    display: none;
  }
`;

export const FloatingMenu = styled.div<{ isOpen: boolean; expanded?: boolean }>`
  position: fixed;
  bottom: 88px;
  right: 20px;
  width: ${props => props.expanded ? '420px' : '280px'};
  background: ${props => props.theme.colors.background};
  border-radius: 16px;
  border: 1px solid ${props => props.theme.colors.inputBorder};
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
  padding: 1rem;
  z-index: 999;
  height: ${props => props.expanded ? '640px' : 'auto'};
  overflow: auto;
  display: flex;
  flex-direction: column;
  transform: ${props => props.isOpen ? 'translateY(0) scale(1)' : 'translateY(20px) scale(0.95)'};
  opacity: ${props => props.isOpen ? 1 : 0};
  visibility: ${props => props.isOpen ? 'visible' : 'hidden'};
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  transform-origin: bottom right;
  overflow-x: hidden;

  @media (max-width: 1200px) {
    width: ${props => props.expanded ? '380px' : '280px'};
  }

  @media (max-width: 768px) {
    width: calc(100vw - 32px);
    right: 16px;
    bottom: 76px;
    transform-origin: bottom center;
    max-height: calc(100vh - 64px);
  }

  @media (max-width: 320px) {
    width: calc(100vw - 24px);
    right: 12px;
  }

  @media (max-width: 723px) {
    display: none;
  }

  ${media.tabletOnly} {
    right: 16px;
    bottom: 84px;
    width: ${props => props.expanded ? '340px' : '260px'};
    max-height: calc(100vh - 96px);
  }
`;

export const MenuItemContainer = styled.div`
  margin-bottom: 0.75rem;

  &:last-child {
    margin-bottom: 0;
  }

  &:first-child {
    margin-bottom: 1rem;
  }
`;

export const FilterPanel = styled.div`
  width: 100%;
  flex: 1 1 auto;
  overflow-y: auto;
  padding: 0.25rem 0 0.5rem 0;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;

  padding-bottom: 72px;
  max-height: none;

  @media (max-width: 480px) {
    max-height: calc(100vh - 120px);
  }
`;

export const FilterHeader = styled.h3`
  margin: 0 0 8px 0;
  font-size: 1rem;
  font-weight: 700;
  color: ${props => props.theme.colors.text};
  padding: 0.25rem 0.5rem;
`;

export const FilterSection = styled.div`
  padding: 0.5rem;
  border-bottom: 1px solid ${props => props.theme.colors.inputBorder};
  min-width: 0;
`;

export const FilterSectionTitle = styled.h4`
  margin: 0 0 8px 0;
  font-size: clamp(0.75rem, 1.6vw, 0.85rem);
  color: ${props => props.theme.colors.fieldLabel};
  font-weight: 600;
`;

export const RadioList = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 8px;
  word-break: break-word;
  min-width: 0;

  @media (min-width: 720px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 10px 12px;
  }
`;

export const RadioOption = styled.label`
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  color: ${props => props.theme.colors.text};
  font-size: clamp(0.7rem, 1.5vw, 0.82rem);

  @media (max-width: 480px) {
    gap: 8px;
    font-size: 0.72rem;
  }
  flex-wrap: wrap;
  white-space: normal;
`;

export const ColorDot = styled.span<{ color?: string }>`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  display: inline-block;
  background: ${props => props.color || '#ccc'};
  box-shadow: 0 1px 4px rgba(2,6,23,0.06);
`;

export const RangeInputs = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  width: 100%;

  @media (max-width: 480px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

export const RangeInput = styled.input`
  width: 100%;
  padding: 0.5rem 0.75rem;
  border-radius: 10px;
  border: 1px solid ${props => props.theme.colors.inputBorder};
  background: ${props => props.theme.colors.searchInputBackground};
  color: ${props => props.theme.colors.text};
  font-size: 0.9rem;

  @media (max-width: 480px) {
    padding: 0.6rem 0.75rem;
    font-size: 0.9rem;
  }
`;

export const ApplyFiltersButton = styled.button`
  display: block;
  margin: 0;
  border-radius: 10px;
  border: none;
  background: ${props => props.theme.colors.primary};
  color: #fff;
  font-weight: 700;
  cursor: pointer;
  padding: 0.6rem 1.25rem;
  margin-top: 8px;
  transition: all 0.2s ease;
  border-radius: 12px;
  box-shadow: 0 8px 20px rgba(0,0,0,0.12);
  width: 360px;
  max-width: calc(100% - 32px);
  text-align: center;
  font-size: 0.95rem;
`;

export const ApplyWrapper = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 16px;
  z-index: 60;
  display: flex;
  justify-content: center;
`;

export const ApplyBackdrop = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: 72px;
  background: ${props => props.theme.colors.background};
  border-top: 1px solid ${props => props.theme.colors.inputBorder};
  border-bottom-left-radius: 12px;
  border-bottom-right-radius: 0;
  z-index: 54;
  pointer-events: none;
`;

export const SearchInputContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;

  > svg {
    position: absolute;
    left: 12px;
    color: ${props => props.theme.colors.searchInputText};
    opacity: 0.6;
    z-index: 1;
  }
`;

export const SearchInput = styled.input`
  padding: 0.75rem 1rem 0.75rem 40px;
  border-radius: 12px;
  border: 1px solid ${props => props.theme.colors.inputBorder};
  background: ${props => props.theme.colors.searchInputBackground};
  color: ${props => props.theme.colors.searchInputText};
  font-size: 0.9rem;
  width: 100%;
  transition: all 0.2s ease;

  &::placeholder {
    color: ${props => props.theme.colors.searchInputText};
    opacity: 0.6;
  }

  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
    box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
  }
`;

export const FilterButton = styled.button<{ isActive?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 0.5rem;
  background: ${props => (props.isActive ? props.theme.colors.primary : props.theme.colors.searchInputBackground)};
  color: ${props => (props.isActive ? "#fff" : props.theme.colors.iconColor)};
  border: 1px solid ${props => (props.isActive ? props.theme.colors.primary : props.theme.colors.inputBorder)};
  padding: 0.75rem 1rem;
  border-radius: 12px;
  cursor: pointer;
  font-size: 0.9rem;
  font-weight: 500;
  width: 100%;
  transition: all 0.2s ease;

  &:hover {
    background: ${props => (props.isActive ? props.theme.colors.primary : props.theme.colors.filterButtonHover)};
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }
`;

export const ThemeToggleButton = styled.button`
  background: ${props => props.theme.colors.searchInputBackground};
  color: ${props => props.theme.colors.iconColor};
  border: 1px solid ${props => props.theme.colors.inputBorder};
  border-radius: 12px;
  width: 100%;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 0.5rem;
  padding: 0 1rem;
  cursor: pointer;
  font-size: 0.9rem;
  font-weight: 500;
  transition: all 0.2s ease;

  > svg {
    font-size: 1.1rem;
  }

  &:hover {
    background: ${props => props.theme.colors.filterButtonHover};
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }
`;

export const BoardWrapper = styled.div`
  background: ${props => props.theme.mode === 'light' ? '#FFFFFF' : props.theme.colors.primaryKanban};
  color: ${props => props.theme.colors.text};
  padding: 8px 12px;
  margin: 0;
  position: relative;
  overflow: hidden;
`;

export const BoardContent = styled.div`
  display: flex;
  gap: 0.5rem;
  overflow-x: auto;
  overflow-y: hidden;
  padding: 0;
  margin: 0;
  width: 100%;
  height: calc(100vh - 106px);
  align-items: stretch;
  box-sizing: border-box;

  @media (min-width: 724px) and (max-width: 1119px) {
    height: calc(100vh - 64px);
  }
  
  @media (max-width: 723px) {
    gap: 0.5rem;
    padding: 0;
    height: calc(100vh - 126px); 
    flex-direction: row;
    align-items: stretch;
    overflow-x: auto;
    overflow-y: hidden;
  }

  &::-webkit-scrollbar {
    height: 8px;
  }
  
  &::-webkit-scrollbar-thumb {
    background-color: ${props => props.theme.colors.scrollThumb};
    border-radius: 4px;
  }
`;

export const blueTones = [
  '#68A1FF',
  '#2d77f7ff',
  '#005FFF',
  '#0049C3',
  '#003EA7',
  '#00348A',
  '#00296F'
];

export const getColumnColor = (index: number) => {
  return blueTones[index % blueTones.length];
};

export const ColumnStyled = styled.div<{ $zoom?: number; $isDropTarget?: boolean }>`
  background: ${props => props.theme.colors.columnBackground};
  border-radius: 12px;
  width: ${props => props.$zoom && props.$zoom < 1 ? 'auto' : '300px'};
  min-width: ${props => props.$zoom && props.$zoom < 1 ? '180px' : '260px'};
  flex: ${props => props.$zoom && props.$zoom < 1 ? '1' : '0 0 300px'};
  border: 1px solid ${props =>
    props.$isDropTarget ? 'rgba(148, 163, 184, 0.55)' : props.theme.colors.inputBorder};
  display: flex;
  overflow: hidden;
  flex-direction: column;
  height: 100%;
  padding-bottom: 0.75rem;
  box-shadow: ${props =>
    props.$isDropTarget
      ? '0 0 0 2px rgba(148, 163, 184, 0.18), 0 8px 22px rgba(15, 23, 42, 0.06)'
      : '0 6px 18px rgba(15, 23, 42, 0.06)'};
  transition: flex 0.2s ease, min-width 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;

  @media (max-width: 723px) {
    height: 100%;
  }
  
  @media (max-width: 640px) {
    height: 100%;
    padding-bottom: 0.5rem;
  }

`;

export const ColumnHeader = styled.div<{ $columnIndex?: number }>`
  background: ${props => props.theme.colors.background};
  font-weight: 700;
  font-size: 0.8rem;
  padding: 0.5rem 0.6rem;
  color: ${props => props.theme.colors.text};
  display: flex;
  gap: 0.5rem;
  flex-wrap: nowrap;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid ${props => props.theme.colors.inputBorder};

  @media (min-width: 768px) {
    font-size: 0.8rem;
    padding: 0.5rem 0.6rem;
  }
`;

export const ColumnHeaderTitle = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

export const ColumnHeaderSubtitle = styled.span`
  font-size: 0.8rem;
  font-weight: 500;
  color: ${props => props.theme.colors.fieldValue};
  opacity: 0.9;
`;

export const AddCardButton = styled.button`
  background: none;
  color: ${props => props.theme.colors.primary};
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  border-radius: 6px;
  transition: opacity 0.15s ease, transform 0.1s ease;

  &:hover {
    opacity: 0.7;
    transform: scale(1.15);
  }

  svg {
    width: 16px;
    height: 16px;
    stroke-width: 2.5px;
  }
`;

export const ColumnFooter = styled.div`
  padding: 0.6rem 1rem;
  border-top: 1px dashed ${props => props.theme.colors.inputBorder};
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: flex-start;
`;

export const FooterAddCard = styled.button`
  background: none;
  border: none;
  color: ${props => props.theme.colors.iconColor};
  padding: 0.4rem 0.55rem;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 600;
  font-size: 0.86rem;
  display: flex;
  gap: 0.5rem;
  align-items: center;
  transition: background 0.12s ease, transform 0.08s ease;

  &:hover {
    background: ${props => props.theme.colors.searchInputBackground};
    transform: translateY(-2px);
  }
`;

export const ColumnCountBadge = styled.span`
  background-color: ${props => props.theme.colors.cardBackground || '#fff'};
  border-radius: 10px;
  padding: 3px 8px;
  font-size: 0.78rem;
  font-weight: 700;
  color: ${props => props.theme.colors.text || '#333'};
  border: 1px solid ${props => props.theme.colors.inputBorder};
  box-shadow: 0 1px 4px rgba(2,6,23,0.03);
  display: inline-flex;
  align-items: center;

  @media (min-width: 768px) {
    font-size: 0.82rem;
    padding: 4px 10px;
  }
`;

export const CardsContainer = styled.div<{ $isDropTarget?: boolean; $empty?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  flex: 1;
  overflow-y: auto;
  min-height: ${props => (props.$empty ? '96px' : '0')};
  padding: 0.5rem 0.5rem 2rem 0.5rem;
  border-radius: 0 0 10px 10px;
  transition: background 0.2s ease;
  ${props =>
    props.$isDropTarget &&
    css`
      background: ${props.theme.mode === 'dark'
        ? 'rgba(148, 163, 184, 0.08)'
        : 'rgba(148, 163, 184, 0.05)'};
    `}

  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-thumb {
    background-color: ${props => props.theme.colors.scrollThumb};
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-track {
    background: ${props => props.theme.colors.scrollTrack};
    border-radius: 3px;
  }
  
  scrollbar-width: thin;
  scrollbar-color: ${props => props.theme.colors.scrollThumb} ${props => props.theme.colors.scrollTrack};

  @media (max-width: 480px) {
    &::-webkit-scrollbar {
      width: 4px;
    }
  }
`;

const getPriorityColor = (priority?: string) => {
  switch (priority) {
    case 'urgent':
      return '#00296F';
    case 'important':
      return '#005FFF';
    case 'not important':
      return '#68A1FF';
    default:
      return 'transparent';
  }
};

const warningPulse = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(244, 67, 54, 0.30); }
  70% { box-shadow: 0 0 0 10px rgba(244, 67, 54, 0); }
  100% { box-shadow: 0 0 0 0 rgba(244, 67, 54, 0); }
`;

export const CardStyled = styled.div<{
  $isOver?: boolean;
  $isGhost?: boolean;
  $isOverlay?: boolean;
  $priority?: string;
  $warning?: boolean;
}>`
  background-color: ${props => props.theme.colors.cardBackground};
  border: ${props => {
    if (props.$warning) return '1px solid #f44336';
    if (props.$isOverlay) return '1px solid rgba(148, 163, 184, 0.45)';
    if (props.$isGhost) return '1px dashed rgba(148, 163, 184, 0.4)';
    return props.$isOver ? '2px dashed rgba(148, 163, 184, 0.5)' : `1px solid ${props.theme.colors.cardBorder}`;
  }};
  border-left: 4px solid ${props => getPriorityColor(props.$priority ?? '')};
  border-radius: 12px;
  padding: 1rem;
  font-size: 0.9rem;
  box-shadow: ${props => {
    if (props.$isOverlay) {
      return '0 14px 36px rgba(15, 23, 42, 0.1), 0 4px 14px rgba(15, 23, 42, 0.05)';
    }
    if (props.$isGhost) {
      return 'inset 0 0 0 1px rgba(148, 163, 184, 0.1)';
    }
    return '0 4px 12px rgba(15, 23, 42, 0.03)';
  }};
  cursor: ${props => (props.$isOverlay ? 'grabbing' : 'grab')};
  transition: box-shadow 0.18s ease, transform 0.12s ease, background 0.18s ease, border 0.18s ease,
    opacity 0.15s ease;
  max-height: 220px;
  overflow-y: auto;
  opacity: ${props => (props.$isGhost ? 0.48 : 1)};
  ${props =>
    props.$isOverlay &&
    css`
      transform: rotate(0.8deg) scale(1.012);
    `}
  ${props =>
    props.$warning &&
    css`
      animation: ${warningPulse} 1.15s ease-out 2;
    `}
  
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-thumb {
    background-color: ${props => props.theme.colors.scrollThumb};
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-track {
    background: ${props => props.theme.colors.scrollTrack};
    border-radius: 3px;
  }
  
  scrollbar-width: thin;
  scrollbar-color: ${props => props.theme.colors.scrollThumb} ${props => props.theme.colors.scrollTrack};
  
  &:hover {
    ${props =>
      !props.$isGhost &&
      !props.$isOverlay &&
      css`
        box-shadow: 0 10px 30px rgba(2, 6, 23, 0.06);
        transform: translateY(-4px);
      `}
  }

  @media (min-width: 768px) {
    padding: 1rem;
    font-size: 0.9rem;
    max-height: 180px;
  }

  @media (max-width: 480px) {
    &::-webkit-scrollbar {
      width: 4px;
    }
  }
`;

/** Preview flutuante no DragOverlay (largura alinhada à coluna Kanban). */
export const KanbanDragOverlayCardWrap = styled.div`
  width: min(280px, calc(100vw - 48px));
  pointer-events: none;
`;

export const CardDescription = styled.p`
  color: ${props => props.theme.colors.text};
  margin: 0;
  font-size: 0.75rem;
  font-weight: 600;

  @media (min-width: 768px) {
    font-size: 0.8rem;
  }
`;

export const CustomField = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 0.35rem;
  font-size: 0.7rem;
  color: ${props => props.theme.colors.text};

  @media (min-width: 768px) {
    font-size: 0.75rem;
  }
`;

export const CustomFieldLabel = styled.span`
  font-weight: 500;
  color: ${props => props.theme.colors.fieldLabel};
  margin-bottom: 2px;
`;

export const CustomFieldValue = styled.span`
  font-weight: 400;
  color: ${props => props.theme.colors.fieldValue};
`;

export const CardDate = styled.span`
  display: block;
  margin-top: 10px;
  font-size: 0.75rem;
  color: #999;
  text-align: right;
`;

export const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.55);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  padding: 0.5rem;

  @media (max-width: 724px) {
    padding: 6px;
    align-items: center;
  }
`;

export const ModalContent = styled.div`
  background: ${props => props.theme.colors.modalBackground || '#fff'};
  color: ${props => props.theme.colors.text};
  padding: 1.2rem;
  border-radius: 14px;
  width: 92vw;
  max-height: 96vh;
  overflow: hidden;
  position: relative;
  box-shadow: 0 18px 50px rgba(2,6,23,0.35);
  max-width: 1100px;
  display: flex;
  gap: 1.25rem;
  align-items: flex-start;

  @media (min-width: 768px) {
    padding: 1.8rem;
    border-radius: 16px;
  }
  @media (min-width: 480px) and (max-width: 724px) {
    width: 98vw;
    max-width: 98vw;
    max-height: 96vh;
    border-radius: 12px;
    padding: 20px;
    overflow-y: auto;
    overflow-x: hidden;
    gap: 1.05rem;
  }

  @media (min-width: 480px) and (max-width: 680px) {
    max-height: calc(100vh - 8px);
    padding: 12px;
    gap: 10px;
    overflow-y: hidden;
  }

  @media (max-width: 479px) {
    width: 98vw;
    max-width: 98vw;
    max-height: 96vh;
    border-radius: 10px;
    padding: 12px;
    overflow-y: auto;
    overflow-x: hidden;
    flex-direction: column;
    gap: 16px;
  }
`;

export const ModalHeader = styled.h2`
  margin: 0 0 1rem 0;
  font-weight: 700;
  color: ${props => props.theme.colors.text};
  font-size: 1.15rem;
  margin-bottom: 18px;
  padding-bottom: 0.25rem;
  display: flex;
  align-items: center;
  gap: 12px;

  &::before {
    content: '';
    width: 10px;
    height: 10px;
    background: ${props => props.theme.colors.primary};
    border-radius: 50%;
    display: inline-block;
  }

  @media (min-width: 768px) {
    font-size: 1.25rem;
  }

  @media (min-width: 480px) and (max-width: 724px) {
    font-size: 1.08rem;
    margin-bottom: 15px;
    gap: 10px;
  }

  @media (min-width: 480px) and (max-width: 680px) {
    font-size: 0.95rem;
    margin-bottom: 10px;
    gap: 8px;
  }
`;

export const ModalCloseButton = styled.button`
  position: absolute;
  top: 14px;
  right: 14px;
  background: transparent;
  border: 1px solid transparent;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  cursor: pointer;
  color: ${props => props.theme.colors.text};
  opacity: 0.95;
  transition: background 0.12s ease, transform 0.08s ease, box-shadow 0.12s ease;

  &:hover {
    transform: translateY(-2px);
    background: ${props => props.theme.mode === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)'};
    box-shadow: 0 6px 18px rgba(2,6,23,0.06);
  }

  @media (max-width: 724px) {
    width: 44px;
    height: 44px;

    &:hover {
      transform: none;
      box-shadow: none;
    }
  }
`;

export const FieldGroup = styled.div`
  margin-bottom: 0.8rem;

  @media (min-width: 480px) and (max-width: 680px) {
    margin-bottom: 0.35rem;
  }
`;

export const FieldLabel = styled.label`
  display: block;
  margin-bottom: 0.35rem;
  font-weight: 600;
  color: ${props => props.theme.colors.fieldLabel};
  font-size: 0.85rem;

  @media (min-width: 480px) and (max-width: 724px) {
    font-size: 0.78rem;
    margin-bottom: 0.3rem;
  }

  @media (min-width: 480px) and (max-width: 680px) {
    font-size: 0.72rem;
    margin-bottom: 0.22rem;
  }
`;

export const FieldTextarea = styled.textarea`
  box-sizing: border-box;
  width: 100%;
  padding: 0.5rem 0.65rem;
  border: 1px solid ${props => props.theme.colors.inputBorder};
  border-radius: 12px;
  font-size: 0.8rem;
  background-color: ${props => props.theme.colors.searchInputBackground};
  color: ${props => props.theme.colors.text};
  resize: vertical;
  min-height: 68px;
  transition: border-color 0.14s ease, box-shadow 0.12s ease;

  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
    box-shadow: 0 6px 18px rgba(2,6,23,0.06);
  }

  &::placeholder {
    color: ${props => props.theme.colors.text};
    opacity: 0.5;
  }

  @media (min-width: 480px) and (max-width: 680px) {
    font-size: 0.68rem;
    padding: 0.35rem 0.45rem;
    min-height: 52px;
  }
`;

export const FieldSelect = styled.select`
  box-sizing: border-box;
  width: 100%;
  min-height: 38px;
  padding: 0.5rem 0.65rem;
  border: 1px solid ${props => props.theme.colors.inputBorder};
  border-radius: 12px;
  font-size: 0.8rem;
  background-color: ${props => props.theme.colors.searchInputBackground};
  color: ${props => props.theme.colors.text};
  transition: border-color 0.14s ease, box-shadow 0.12s ease;

  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
    box-shadow: 0 6px 18px rgba(2,6,23,0.06);
  }

  option {
    background-color: ${props => props.theme.colors.searchInputBackground};
    color: ${props => props.theme.colors.text};
  }

  @media (min-width: 480px) and (max-width: 680px) {
    min-height: 30px;
    padding: 0.35rem 0.45rem;
    font-size: 0.68rem;
  }
`;

export const FieldInput = styled.input`
  box-sizing: border-box;
  width: 100%;
  min-height: 38px;
  padding: 0.5rem 0.65rem;
  border: 1px solid ${props => props.theme.colors.inputBorder};
  border-radius: 12px;
  font-size: 0.8rem;
  background-color: ${props => props.theme.colors.searchInputBackground};
  color: ${props => props.theme.colors.text};
  transition: border-color 0.14s ease, box-shadow 0.12s ease;

  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
    box-shadow: 0 6px 18px rgba(2,6,23,0.06);
  }

  &::placeholder {
    color: ${props => props.theme.colors.text};
    opacity: 0.56;
  }

  &[type='date'],
  &[type='datetime-local'] {
    appearance: none;
    -webkit-appearance: none;
    min-height: 38px;
    padding-right: 0.6rem;
  }

  &[type='date']::-webkit-calendar-picker-indicator,
  &[type='datetime-local']::-webkit-calendar-picker-indicator {
    opacity: 0.68;
    cursor: pointer;
    border-radius: 8px;
    padding: 2px;
    transition: opacity 0.12s ease, transform 0.12s ease;
  }

  &[type='date']:hover::-webkit-calendar-picker-indicator,
  &[type='datetime-local']:hover::-webkit-calendar-picker-indicator {
    opacity: 0.9;
    transform: scale(1.04);
  }

  &[type='date']::-webkit-datetime-edit,
  &[type='datetime-local']::-webkit-datetime-edit {
    color: ${props => props.theme.colors.text};
    opacity: 0.92;
  }

  @media (min-width: 480px) and (max-width: 680px) {
    min-height: 30px;
    padding: 0.35rem 0.45rem;
    font-size: 0.68rem;

    &[type='date'],
    &[type='datetime-local'] {
      min-height: 30px;
    }
  }
`;

export const BooleanOptionsContainer = styled.div`
  box-sizing: border-box;
  width: 100%;
  min-height: 38px;
  padding: 0.5rem 0.65rem;
  border: 1px solid ${props => props.theme.colors.inputBorder};
  border-radius: 12px;
  background-color: ${props => props.theme.colors.searchInputBackground};
  display: flex;
  align-items: center;
  gap: 0.8rem;
  transition: border-color 0.14s ease, box-shadow 0.12s ease;

  &:focus-within {
    border-color: ${props => props.theme.colors.primary};
    box-shadow: 0 6px 18px rgba(2,6,23,0.06);
  }

  @media (min-width: 480px) and (max-width: 680px) {
    min-height: 30px;
    padding: 0.35rem 0.45rem;
    gap: 0.45rem;
  }
`;

export const BooleanOptionLabel = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  color: ${props => props.theme.colors.text};
  font-size: 0.85rem;
  cursor: pointer;

  input[type='radio'] {
    margin: 0;
    accent-color: ${props => props.theme.colors.primary};
  }

  @media (min-width: 480px) and (max-width: 680px) {
    font-size: 0.68rem;
    gap: 0.25rem;
  }
`;

export const ModalActions = styled.div`
  display: flex;
  gap: 0.75rem;
  justify-content: flex-end;
  margin-top: 1.5rem;

  @media (max-width: 479px) {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 12px;
  }

  @media (min-width: 480px) and (max-width: 680px) {
    margin-top: 0.6rem;
    gap: 0.45rem;
  }
`;

export const CancelButton = styled.button`
  padding: 0.75rem 1.25rem;
  border: 1px solid ${props => props.theme.colors.inputBorder};
  border-radius: 10px;
  background-color: ${props => props.theme.colors.buttonCancelDarkBackground};
  color: ${props => props.theme.colors.buttonCancelDarkText};
  font-size: 0.95rem;
  cursor: pointer;
  transition: background-color 0.12s ease, transform 0.08s ease;

  &:hover {
    transform: translateY(-2px);
  }

  @media (max-width: 479px) {
    width: 100%;
    min-height: 44px;
    padding: 10px 12px;

    &:hover {
      transform: none;
    }
  }

  @media (min-width: 480px) and (max-width: 680px) {
    min-height: 30px;
    padding: 5px 9px;
    font-size: 0.68rem;
  }
`;

export const SubmitButton = styled.button`
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 12px;
  background-color: ${props => props.theme.colors.primary};
  color: white;
  font-size: 0.95rem;
  cursor: pointer;
  transition: background-color 0.12s ease, transform 0.08s ease, box-shadow 0.12s ease;

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(2,6,23,0.12);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  @media (max-width: 479px) {
    width: 100%;
    min-height: 44px;
    padding: 10px 12px;

    &:hover:not(:disabled) {
      transform: none;
      box-shadow: none;
    }
  }

  @media (min-width: 480px) and (max-width: 680px) {
    min-height: 30px;
    padding: 5px 9px;
    font-size: 0.68rem;

    &:hover:not(:disabled) {
      transform: none;
      box-shadow: none;
    }
  }
`;

export const AddFieldButton = styled.button`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid ${props => props.theme.colors.inputBorder};
  border-radius: 8px;
  background-color: ${props => props.theme.colors.searchInputBackground};
  color: ${props => props.theme.colors.text};
  font-size: 0.9rem;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: ${props => props.theme.colors.filterButtonHover};
  }
`;

export const ScrollContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  margin-bottom: 1rem;
  max-height: calc(96vh - 200px);

  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-thumb {
    background-color: ${props => props.theme.colors.scrollThumb};
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-track {
    background: ${props => props.theme.colors.scrollTrack};
    border-radius: 3px;
  }

  @media (max-width: 479px) {
    max-height: none;
    margin-bottom: 0;
  }

  @media (min-width: 480px) and (max-width: 680px) {
    max-height: none;
    overflow-y: hidden;
    margin-bottom: 0;
  }
`;


export const AddFieldModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
  box-sizing: border-box;
`;

export const AddFieldModalContent = styled.div`
  background-color: ${props => props.theme.colors.modalBackground};
  color: ${props => props.theme.colors.text};
  border-radius: 8px;
  padding: 24px;
  width: 100%;
  max-width: 500px;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
  border: 1px solid ${props => props.theme.colors.inputBorder};
  
  @media (max-width: 768px) {
    padding: 20px;
    margin: 10px;
    max-width: calc(100% - 20px);
  }
  
  @media (max-width: 480px) {
    padding: 16px;
    border-radius: 6px;
  }
`;

export const AddFieldModalCloseButton = styled.button`
  position: absolute;
  top: 12px;
  right: 16px;
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: ${props => props.theme.colors.text};
  opacity: 0.7;
  padding: 4px;
  line-height: 1;
  transition: color 0.2s ease, opacity 0.2s ease;
  
  &:hover {
    opacity: 1;
    color: ${props => props.theme.colors.text};
  }
  
  &:focus {
    outline: 2px solid ${props => props.theme.colors.primary};
    outline-offset: 2px;
  }
  
  @media (max-width: 480px) {
    top: 8px;
    right: 12px;
    font-size: 20px;
  }
`;

export const AddFieldModalHeader = styled.h2`
  margin: 0 0 20px 0;
  font-size: 20px;
  font-weight: 600;
  color: ${props => props.theme.colors.text};
  padding-right: 40px;
  border-bottom: 1px solid ${props => props.theme.colors.inputBorder};
  padding-bottom: 12px;
  
  @media (max-width: 480px) {
    font-size: 18px;
    margin-bottom: 16px;
    padding-right: 35px;
  }
`;

export const AddFieldGroup = styled.div`
  margin-bottom: 20px;
  max-height: 120px;
  overflow-y: auto;
  
  @media (max-width: 480px) {
    margin-bottom: 16px;
  }
`;

export const AddFieldLabel = styled.label`
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
  color: ${props => props.theme.colors.fieldLabel};
  font-size: 14px;
  
  @media (max-width: 480px) {
    font-size: 13px;
    margin-bottom: 6px;
  }
`;

export const AddFieldSelect = styled.select`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid ${props => props.theme.colors.inputBorder};
  border-radius: 4px;
  font-size: 14px;
  background-color: ${props => props.theme.colors.searchInputBackground};
  color: ${props => props.theme.colors.text};
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
  max-height: 100px;
  overflow-y: auto;
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
    box-shadow: 0 0 0 2px rgba(74, 144, 226, 0.25);
  }
  
  &:disabled {
    background-color: ${props => props.theme.colors.filterButtonBackground};
    color: ${props => props.theme.colors.text};
    opacity: 0.6;
    cursor: not-allowed;
  }
  
  option {
    padding: 8px;
    background-color: ${props => props.theme.colors.searchInputBackground};
    color: ${props => props.theme.colors.text};
    
    &:hover {
      background-color: ${props => props.theme.colors.filterButtonHover};
    }
    
    &:checked {
      background-color: ${props => props.theme.colors.primary};
      color: white;
    }
  }
  
  @media (max-width: 480px) {
    padding: 12px;
    font-size: 16px; 
    max-height: 150px;
  }
`;

export const AddFieldModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 24px;
  padding-top: 16px;
  border-top: 1px solid ${props => props.theme.colors.inputBorder};
  
  @media (max-width: 480px) {
    flex-direction: column-reverse;
    gap: 8px;
    margin-top: 20px;
  }
`;

export const AddFieldSubmitButton = styled.button`
  background-color: ${props => props.theme.colors.primary};
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s ease, transform 0.1s ease;
  min-width: 100px;
  
  &:hover:not(:disabled) {
    background-color: ${props => props.theme.colors.primary};
    opacity: 0.9;
  }
  
  &:active:not(:disabled) {
    transform: translateY(1px);
  }
  
  &:disabled {
    background-color: ${props => props.theme.colors.primary};
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
  
  &:focus {
    outline: 2px solid ${props => props.theme.colors.primary};
    outline-offset: 2px;
  }
  
  @media (max-width: 480px) {
    width: 100%;
    padding: 12px 20px;
    font-size: 16px;
  }
`;

export const AddFieldCancelButton = styled.button`
  background-color: ${props => props.theme.colors.buttonCancelDarkBackground};
  color: ${props => props.theme.colors.buttonCancelDarkText};
  border: 1px solid ${props => props.theme.colors.buttonCancelDarkBorder};
  padding: 10px 20px;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s ease, transform 0.1s ease;
  min-width: 100px;
  
  &:hover {
    background-color: ${props => props.theme.colors.filterButtonHover};
  }
  
  &:active {
    transform: translateY(1px);
  }
  
  &:focus {
    outline: 2px solid ${props => props.theme.colors.inputBorder};
    outline-offset: 2px;
  }
  
  @media (max-width: 480px) {
    width: 100%;
    padding: 12px 20px;
    font-size: 16px;
  }
`;

export const ModalWrapper = styled.div`
  display: flex;
  width: 100%;

  @media (max-width: 479px) {
    flex-direction: column;
    gap: 16px;
  }
`;

export const MainContent = styled.div`
  flex: 1;
  padding-right: 20px;
  border-right: 1px solid ${props => props.theme.colors.sidebarBorder};
  display: flex;
  flex-direction: column;

  @media (min-width: 480px) and (max-width: 724px) {
    padding-right: 17px;
  }

  @media (min-width: 480px) and (max-width: 680px) {
    padding-right: 10px;
  }

  @media (max-width: 479px) {
    width: 100%;
    padding-right: 0;
    border-right: none;
  }
`;

export const Sidebar = styled.div`
  flex: 0 0 250px;
  padding-left: 20px;

  @media (min-width: 480px) and (max-width: 724px) {
    flex: 0 0 220px;
    padding-left: 17px;
  }

  @media (min-width: 480px) and (max-width: 680px) {
    flex: 0 0 175px;
    padding-left: 10px;
  }

  @media (max-width: 479px) {
    width: 100%;
    flex: 0 0 auto;
    padding-left: 0;
  }
`;

export const SidebarSection = styled.div`
  margin-bottom: 25px;

  @media (min-width: 480px) and (max-width: 724px) {
    margin-bottom: 21px;
  }

  @media (min-width: 480px) and (max-width: 680px) {
    margin-bottom: 10px;
  }

  @media (max-width: 479px) {
    width: 100%;
    margin-bottom: 16px;
  }
`;

export const SidebarTitle = styled.h3`
  color: ${props => props.theme.colors.text};
  font-size: 0.9rem;
  font-weight: 600;
  margin-bottom: 12px;
  border-bottom: 1px solid ${props => props.theme.colors.sidebarTitleBorder};
  padding-bottom: 8px;

  @media (min-width: 480px) and (max-width: 724px) {
    font-size: 0.8rem;
    margin-bottom: 10px;
    padding-bottom: 7px;
  }

  @media (min-width: 480px) and (max-width: 680px) {
    font-size: 0.7rem;
    margin-bottom: 6px;
    padding-bottom: 5px;
  }
`;

export const NotesTextarea = styled(FieldTextarea)`
  width: 100%;
  min-height: 100px;
  margin-bottom: 10px;
  border-color: ${props => props.theme.colors.inputBorder};
  font-size: 0.85rem;
  padding: 8px;
  background-color: ${props => props.theme.colors.cardBackground};
  color: ${props => props.theme.colors.text};

  @media (min-width: 480px) and (max-width: 680px) {
    min-height: 62px;
    margin-bottom: 6px;
    padding: 6px;
    font-size: 0.7rem;
  }
`;


export const ClickableField = styled.div`
  padding: 8px 12px;
  background-color: ${props => props.theme.colors.clickableFieldBackground};
  border-radius: 6px;
  border: 1px solid ${props => props.theme.colors.clickableFieldBorder};
  min-height: 38px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  position: relative;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: ${props => props.theme.colors.clickableFieldHover};
  }

  &:hover > span:last-child {
    opacity: 1;
  }
`;

export const ClickableFieldText = styled.span`
  color: ${props => props.theme.colors.text};
`;

export const ClickableFieldHint = styled.span`
  font-size: 0.7rem;
  color: ${props => props.theme.colors.clickableFieldHintText};
  background-color: ${props => props.theme.colors.clickableFieldHintBackground};
  padding: 2px 8px;
  border-radius: 10px;
  opacity: 0;
  transition: opacity 0.2s ease;
`;

export const EmptyFieldsMessage = styled.p`
  text-align: center;
  color: ${props => props.theme.colors.emptyMessageText};
  font-style: italic;
  margin-top: 20px;
`;

export const ModalFooter = styled.div`
  flex-shrink: 0;
  margin-top: 20px;
  border-top: 1px solid ${props => props.theme.colors.modalFooterBorder};
  padding-top: 15px;

  ${ModalActions} {
    justify-content: flex-end;
    margin-top: 0;
  }

  ${SubmitButton} {
    background-color: #00348A;
    border-radius: 16px;
    font-size: 0.85rem;
    font-weight: 600;
    padding: 6px 16px;
  }

  ${CancelButton} {
    border: 1px solid ${props => props.theme.colors.buttonCancelDarkBorder};
    background-color: ${props => props.theme.colors.buttonCancelDarkBackground};
    color: ${props => props.theme.colors.buttonCancelDarkText};
    border-radius: 16px;
    font-size: 0.85rem;
    font-weight: 600;
    padding: 6px 16px;
    margin-left: 8px;
  }

  @media (min-width: 480px) and (max-width: 724px) {
    margin-top: 17px;
    padding-top: 13px;
  }

  @media (min-width: 480px) and (max-width: 680px) {
    margin-top: 10px;
    padding-top: 8px;

    ${SubmitButton} {
      font-size: 0.7rem;
      padding: 4px 10px;
    }

    ${CancelButton} {
      font-size: 0.7rem;
      padding: 4px 10px;
    }
  }

  @media (max-width: 479px) {
    margin-top: 16px;
    padding-top: 12px;

    ${CancelButton} {
      margin-left: 0;
    }
  }
`;
export const ErrorMessage = styled.p`
  color: #ff4d4d;
  font-size: 0.85rem;
  margin-top: 0.5rem;
`;

export const NewModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
  box-sizing: border-box;
`;

// Substitui ModalContent
export const NewModalContent = styled.div`
  background-color: white;
  border-radius: 8px;
  padding: 24px;
  width: 100%;
  max-width: 500px;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
  
  @media (max-width: 768px) {
    padding: 20px;
    margin: 10px;
    max-width: calc(100% - 20px);
  }
  
  @media (max-width: 480px) {
    padding: 16px;
    border-radius: 6px;
  }
`;

// Substitui ModalCloseButton
export const NewModalCloseButton = styled.button`
  position: absolute;
  top: 12px;
  right: 16px;
  background: transparent;
  border: 1px solid transparent;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  cursor: pointer;
  color: ${props => props.theme.colors.text || '#666'};
  padding: 4px;
  line-height: 1;
  transition: color 0.12s ease, background 0.12s ease, box-shadow 0.12s ease;

  &:hover {
    color: ${props => props.theme.colors.text || '#333'};
    background: ${props => props.theme.mode === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)'};
    box-shadow: 0 6px 18px rgba(2,6,23,0.06);
  }

  &:focus {
    outline: 2px solid ${props => props.theme.colors.inputFocus || '#007bff'};
    outline-offset: 2px;
  }
  
  @media (max-width: 480px) {
    top: 8px;
    right: 12px;
    font-size: 20px;
  }
`;

// Substitui ModalHeader
export const NewModalHeader = styled.h2`
  margin: 0 0 20px 0;
  font-size: 20px;
  font-weight: 600;
  color: #333;
  padding-right: 40px;
  
  @media (max-width: 480px) {
    font-size: 18px;
    margin-bottom: 16px;
    padding-right: 35px;
  }
`;

// Substitui FieldGroup
export const NewFieldGroup = styled.div`
  margin-bottom: 20px;
  
  @media (max-width: 480px) {
    margin-bottom: 16px;
  }
`;

// Substitui FieldLabel
export const NewFieldLabel = styled.label`
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
  color: #555;
  font-size: 14px;
  
  @media (max-width: 480px) {
    font-size: 13px;
    margin-bottom: 6px;
  }
`;

// Substitui FieldSelect
export const NewFieldSelect = styled.select`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  background-color: white;
  color: #333;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
  max-height: 200px;
  overflow-y: auto;
  
  &:focus {
    outline: none;
    border-color: #007bff;
    box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
  }
  
  &:disabled {
    background-color: #f8f9fa;
    color: #6c757d;
    cursor: not-allowed;
  }
  
  option {
    padding: 8px;
    background-color: white;
    color: #333;
    
    &:hover {
      background-color: #f8f9fa;
    }
    
    &:checked {
      background-color: #007bff;
      color: white;
    }
  }
  
  @media (max-width: 480px) {
    padding: 12px;
    font-size: 16px;
    max-height: 150px;
  }
`;

// Substitui ModalActions
export const NewModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 24px;
  
  @media (max-width: 480px) {
    flex-direction: column-reverse;
    gap: 8px;
    margin-top: 20px;
  }
`;

// Substitui SubmitButton
export const NewSubmitButton = styled.button`
  background-color: #007bff;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s ease, transform 0.1s ease;
  min-width: 100px;
  
  &:hover:not(:disabled) {
    background-color: #0056b3;
  }
  
  &:active:not(:disabled) {
    transform: translateY(1px);
  }
  
  &:disabled {
    background-color: #6c757d;
    cursor: not-allowed;
    transform: none;
  }
  
  &:focus {
    outline: 2px solid #007bff;
    outline-offset: 2px;
  }
  
  @media (max-width: 480px) {
    width: 100%;
    padding: 12px 20px;
    font-size: 16px;
  }
`;

// Substitui CancelButton
export const NewCancelButton = styled.button`
  background-color: #0056b3;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s ease, transform 0.1s ease;
  min-width: 100px;
  
  &:hover {
    background-color: #004085;
  }
  
  &:active {
    transform: translateY(1px);
  }
  
  &:focus {
    outline: 2px solid #0056b3;
    outline-offset: 2px;
  }
  
  @media (max-width: 480px) {
    width: 100%;
    padding: 12px 20px;
    font-size: 16px;
  }
`;

interface FavoriteButtonProps {
  isFavorite: boolean;
}

export const FavoriteButton = styled.button<{ isFavorite: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 5px 6px;
  background: transparent;
  color: ${props => (props.isFavorite ? props.theme.colors.primary || '#c00' : props.theme.colors.text)};
  border: 1px solid ${props => (props.isFavorite ? props.theme.colors.primary || '#c00' : 'transparent')};
  border-radius: 8px;
  cursor: pointer;
  font-weight: 600;
  font-size: 0.8rem;
  transition: background 0.12s ease, transform 0.08s ease, box-shadow 0.12s ease;
  margin-top: 6px;

  &:hover {
    background: ${props => (props.isFavorite ? 'rgba(0,0,0,0.02)' : props.theme.colors.searchInputBackground)};
    transform: translateY(-2px);
    box-shadow: 0 6px 18px rgba(2,6,23,0.04);
  }

  @media (max-width: 724px) {
    width: 100%;
    min-height: 44px;
    padding: 10px 12px;

    &:hover {
      transform: none;
      box-shadow: none;
    }
  }

  @media (min-width: 480px) and (max-width: 680px) {
    min-height: 32px;
    padding: 4px 8px;
    font-size: 0.65rem;
    margin-top: 4px;
  }
`;
